import { useEffect, useState } from 'react';
import { webApiClient } from '../../../lib/authentication';

const PREVIEW_PATH = '/api/pdf/gwa_postcard_preview.pdf';

// Wide enough that the lightbox image stays crisp; a 4.25x6 portrait page lands at ~1000x1412.
const RENDER_WIDTH_PX = 1000;

export interface AdoptionCardPreviews {
  loading: boolean;
  error: boolean;
  /** Data URLs of the rendered card faces, ready for <img src>. */
  front: string | null;
  back: string | null;
  /** Object URL of the raw preview PDF — fallback link when rasterizing fails. */
  pdfUrl: string | null;
}

/**
 * Fetches the personalized card-preview PDF through the authenticated axios client (a plain
 * <img>/<object> src would not carry the x-session-id fallback header) and rasterizes its two
 * pages with pdf.js. pdf.js is imported lazily so only this page pays for the bundle, and
 * `enabled` keeps even that off the wire for rescues outside the cards beta.
 */
export function useAdoptionCardPreviews(enabled: boolean): AdoptionCardPreviews {
  const [previews, setPreviews] = useState<AdoptionCardPreviews>({
    loading: true,
    error: false,
    front: null,
    back: null,
    pdfUrl: null,
  });

  useEffect(() => {
    if (!enabled) {
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;

    async function load() {
      const res = await webApiClient.get<ArrayBuffer>(PREVIEW_PATH, { responseType: 'arraybuffer' });
      // Snapshot the blob before pdf.js transfers (and detaches) the underlying buffer to its worker.
      objectUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const [pdfjs, { default: PdfWorker }] = await Promise.all([
        import('pdfjs-dist'),
        import('pdfjs-dist/build/pdf.worker.min.mjs?worker'),
      ]);
      // pdf.js starts its worker as a module Worker, so the browser enforces a JavaScript MIME
      // type on the worker file. `?worker` has Vite bundle it as a `.js` chunk and construct the
      // Worker for us; a `?url` import instead emits `pdf.worker.min.mjs`, which our S3 origin
      // serves as application/octet-stream — the browser refuses to start a module worker on
      // that type, so previews only ever failed once deployed. The port is created per load
      // because `loadingTask.destroy()` below terminates whichever port it was handed.
      pdfjs.GlobalWorkerOptions.workerPort = new PdfWorker();
      const loadingTask = pdfjs.getDocument({ data: new Uint8Array(res.data) });
      const doc = await loadingTask.promise;

      async function renderPage(pageNumber: number) {
        const page = await doc.getPage(pageNumber);
        const scale = RENDER_WIDTH_PX / page.getViewport({ scale: 1 }).width;
        const viewport = page.getViewport({ scale });
        const canvas = document.createElement('canvas');
        canvas.width = Math.ceil(viewport.width);
        canvas.height = Math.ceil(viewport.height);
        await page.render({ canvas, viewport }).promise;
        return canvas.toDataURL('image/png');
      }

      // The print template lays out the personalized detail side as PDF page 1 and the hero art
      // as page 2; the portal presents the hero as the card's Front, matching the artwork.
      const detailFace = await renderPage(1);
      const heroFace = doc.numPages > 1 ? await renderPage(2) : null;
      void loadingTask.destroy();
      if (!cancelled) {
        setPreviews({
          loading: false,
          error: false,
          front: heroFace ?? detailFace,
          back: heroFace ? detailFace : null,
          pdfUrl: objectUrl,
        });
      }
    }

    load().catch(() => {
      if (!cancelled) {
        setPreviews({ loading: false, error: true, front: null, back: null, pdfUrl: objectUrl });
      }
    });

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [enabled]);

  return previews;
}

const SAMPLE_FILE_NAME = 'Fi Gift With Adoption Card.pdf';

/** Downloads the preview PDF reordered to Front, Back (print order is the reverse). */
export async function downloadAdoptionCardSample(pdfUrl: string) {
  const [bytes, { PDFDocument }] = await Promise.all([
    fetch(pdfUrl).then((res) => res.arrayBuffer()),
    import('pdf-lib'),
  ]);
  const source = await PDFDocument.load(bytes);
  const sample = await PDFDocument.create();
  const pageOrder = source.getPageIndices().reverse();
  const pages = await sample.copyPages(source, pageOrder);
  pages.forEach((page) => sample.addPage(page));

  const url = URL.createObjectURL(new Blob([await sample.save()], { type: 'application/pdf' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = SAMPLE_FILE_NAME;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
